import { merge } from 'lodash'

import { getRateLimitHeaders, normalizeError, parseFailedResponse, projectName } from '@sigma-frontend-libs/commons'
import GFetch, { isNativeRequestBodyTypeGuard } from '@sigma-frontend-libs/g-fetch'

import store from '@/store'

import { getClientOptions } from './runtime'

/**
 * Подготавливает параметры запроса перед отправкой через `GFetch`.
 *
 * Функция приводит заголовки и тело к ожидаемому формату: добавляет токен
 * авторизации только если он не задан вручную, выставляет JSON
 * `Content-Type` для обычных тел запроса и сериализует такие тела в строку.
 * Нативные типы вроде `FormData`, `Blob` и `URLSearchParams` остаются без
 * дополнительной обработки.
 *
 * @param params Исходные параметры запроса.
 * @returns Изменённые параметры запроса или `undefined`, если аргумент не передан.
 */
const beforeRequest = params => {
  if (params) {
    const headers = new Headers(params.headers)
    const token = window.localStorage.getItem(`${projectName}:token`) ?? store.getters['authentication/token']
    const hasRequestBody = typeof params.body !== 'undefined'
    const hasNativeRequestBody = isNativeRequestBodyTypeGuard(params.body)
    const hasAuthorizationHeader = Boolean(headers.get('Authorization'))

    if (hasRequestBody && !hasNativeRequestBody && !headers.get('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    if (!hasAuthorizationHeader) {
      if (token) {
        headers.set('Authorization', token)
      } else {
        headers.delete('Authorization')
      }
    }

    params.headers = Object.fromEntries(headers.entries())

    if (hasRequestBody && !hasNativeRequestBody) {
      params.body = JSON.stringify(params.body)
    }

    return params
  }
}

/**
 * Преобразует неуспешный HTTP-ответ в прикладную ошибку клиента.
 *
 * Функция выполняет проектную нормализацию ответа, отдельно обрабатывает
 * служебные сценарии вроде `401` и `429`, а затем выбрасывает ошибку в том
 * формате, который ожидают потребители текущего приложения.
 *
 * @param response Неуспешный HTTP-ответ.
 * @returns Не возвращает значение и всегда завершает выполнение выбрасыванием ошибки.
 */
async function errorRequest(response) {
  const source = await parseFailedResponse(response)
  const error = normalizeError(source, response)
  const errorCode = error.error

  if (response.status === 401 || errorCode === 401) {
    await store.dispatch('login/logout')
  }

  if (response.status === 429 || errorCode === 429) {
    const rateLimitHeaders = getRateLimitHeaders(response)

    throw merge({}, error, rateLimitHeaders)
  }

  throw error
}

/**
 * Создаёт экземпляр `GFetch`, заранее привязанный к текущим настройкам клиента.
 *
 * Функция объединяет переданные опции с базовыми параметрами API, подключает
 * стандартные обработчики подготовки запроса и обработки ошибок, а также
 * позволяет точечно переопределить любой из этих шагов через `hooks`.
 *
 * @param options Частичные настройки клиента, например дополнительный `path`.
 * @param hooks Частичные обработчики жизненного цикла запроса.
 * @returns Настроенный экземпляр `GFetch`.
 */
export const createRequest = (options = {}, hooks = {}) => {
  const clientOptions = getClientOptions(options.path)
  const mergedOptions = merge(options, clientOptions)
  const resolvedHooks = {
    before: hooks.before ?? beforeRequest,
    after: hooks.after,
    error: hooks.error ?? errorRequest
  }

  return new GFetch(mergedOptions, resolvedHooks)
}

export const request = createRequest()
export default request
